import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, PanelLeftClose, Share } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { RootState } from '../../../reducers';
import { useIsMobile } from '../../../hooks/use-mobile';
import { isSpecialFolder } from '../../../constants/folders';
import UpgradeButton from './UpgradeButton';
import UpsellButton from './UpsellButton';
import EducationalDiscount from './EducationalDiscount';
import AssistantToolbar from '../../assistant';
import { SidebarTrigger } from '../../../components/ui/sidebar';
import { AccessAvatarList } from '../../../components/shared/AccessAvatarList';
import { ShareDialog } from '../../../components/shared/ShareDialog';
import { Button } from '../../../components/ui/button';

export const CustomToolbar: React.FC = () => {
  const { t } = useTranslation();
  const open = useSelector(
    (state: RootState) => state.dashboard.drawerState === 'open',
  );
  const selectedVideo = useSelector((state: RootState) => state.selectedVideo);
  const selectedFolder = useSelector(
    (state: RootState) => state.library.selectedFolder,
  );
  const folders = useSelector((state: RootState) => state.library.folders.data);
  const currentUser = useSelector((state: RootState) => state.settings.user);
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isAssistantFocused, setIsAssistantFocused] = useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [hasReceivedInitialState, setHasReceivedInitialState] = useState(false);
  const [isToolbarShareDialogOpen, setIsToolbarShareDialogOpen] =
    useState(false);

  // Determine if we should hide sidebar button
  const shouldHideSidebarButton = isMobile && isAssistantFocused;

  // Show hamburger when on mobile and sidebar is not open
  const shouldShowHamburger = isMobile && !open && !shouldHideSidebarButton;

  // Check if we're in selected video view (same logic as in sidebar/ChatPanel)
  // Use hash for routes since app uses hash router
  const pathParts = (location.hash || location.pathname)
    .replace('#', '')
    .split('/')
    .filter(Boolean);
  const isSelectedVideoView =
    pathParts[0] === 'library' && pathParts.length === 4;
  const isLibraryFolderView =
    pathParts[0] === 'library' && pathParts.length === 3;
  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth < 768;

  // Get current folder from URL if selectedFolder is not set
  const currentFolderId = isLibraryFolderView ? pathParts[2] : null;
  const isSpecialView = currentFolderId
    ? isSpecialFolder(currentFolderId)
    : false;

  const currentFolder = React.useMemo(() => {
    if (selectedFolder) {
      return selectedFolder;
    }
    if (currentFolderId && folders.length > 0 && !isSpecialView) {
      return folders.find((f) => f._id === currentFolderId) || null;
    }
    return null;
  }, [selectedFolder, currentFolderId, folders, isSpecialView]);

  // Helper to check if current user can share
  const canShare = React.useMemo(() => {
    if (isSelectedVideoView && selectedVideo.file) {
      // For files, check if user is owner or if file is in a folder they own
      const { file } = selectedVideo;
      if (file.ownerId === currentUser?._id) return true;

      // If file is in a folder, check folder permissions
      if (currentFolder) {
        return currentFolder.ownerId === currentUser?._id;
      }
      return true; // Allow sharing if no folder context (will be validated by backend)
    }

    if (isLibraryFolderView && currentFolder) {
      // For folders, only owner can modify permissions
      if (isSpecialView) return false; // Can't share special folders
      return currentFolder.ownerId === currentUser?._id;
    }

    return false;
  }, [
    isSelectedVideoView,
    isLibraryFolderView,
    selectedVideo.file,
    currentFolder,
    isSpecialView,
    currentUser?._id,
  ]);

  // For special views (recents, private, etc.), show access list without a folder
  const shouldShowAccessList =
    isLibraryFolderView && (currentFolder || isSpecialView);

  // Chat panel will be open by default in selected video view on desktop
  const shouldChatPanelBeOpenByDefault =
    isSelectedVideoView && !isMobileViewport;

  // Check chat panel state on mount and when location changes
  React.useEffect(() => {
    // Reset state tracking on location change
    setHasReceivedInitialState(false);

    const checkChatPanelState = () => {
      if ((window as any).__chatPanelControls?.isOpen) {
        const isOpen = (window as any).__chatPanelControls.isOpen();
        setIsChatPanelOpen(isOpen);
        setHasReceivedInitialState(true);
      }
    };

    // Check immediately
    checkChatPanelState();

    // Set up an interval to check chat panel state
    const interval = setInterval(checkChatPanelState, 100);

    return () => clearInterval(interval);
  }, [location.pathname]);

  // Handle chat toggle
  const handleChatToggle = () => {
    if ((window as any).__chatPanelControls) {
      (window as any).__chatPanelControls.toggle();
    }
  };

  return (
    <header className={cn('w-full bg-background', 'mb-2')}>
      <div className="flex h-14 items-center px-1 sm:px-2">
        <div
          className={cn(
            'flex w-full items-center justify-between gap-2',
            shouldShowHamburger ? 'pl-0.5' : 'pl-2',
            'pr-1',
          )}
        >
          {/* Hamburger menu - show only when sidebar is in offcanvas mode and closed */}
          {shouldShowHamburger && (
            <SidebarTrigger icon="menu" className="h-10 w-10 p-0" />
          )}

          {/* Avatar List - show in selected video view or folder view */}
          {isSelectedVideoView && selectedVideo.file && (
            <div className="flex-shrink-0">
              <AccessAvatarList item={selectedVideo.file} size="sm" />
            </div>
          )}
          {shouldShowAccessList && (
            <div className="flex-shrink-0">
              <AccessAvatarList folder={currentFolder || undefined} size="sm" />
            </div>
          )}

          {/* Assistant Toolbar - hidden but still rendered */}
          <div className="hidden">
            <AssistantToolbar onFocusChange={setIsAssistantFocused} />
          </div>

          {/* Spacer to push action buttons to the right */}
          <div className="flex-1" />

          {/* Action Buttons - right aligned with consistent spacing */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Share button - show only in selected video view */}
            {isSelectedVideoView && selectedVideo.file && canShare ? (
              <Button
                variant="secondary"
                size="default"
                onClick={() => {
                  setIsToolbarShareDialogOpen(true);
                }}
                className="h-10 px-3 shadow-sm transition-all duration-300 ease-in-out bg-white dark:bg-secondary hover:bg-white/90 dark:hover:bg-secondary/80"
                aria-label={t('common.share')}
              >
                <Share className="mr-1 h-4 w-4" />
                {t('common.share')}
              </Button>
            ) : null}

            <UpsellButton />
            <EducationalDiscount />
            <UpgradeButton />
            {/* Chat toggle button - only show when chat panel is closed */}
            {/* Hide during initial load in selected video view to prevent flicker */}
            {!isChatPanelOpen &&
              (hasReceivedInitialState || !shouldChatPanelBeOpenByDefault) && (
                <button
                  type="button"
                  onClick={handleChatToggle}
                  className="group h-8 w-8 rounded-md hover:bg-accent dark:hover:bg-accent flex items-center justify-center text-foreground hover:cursor-w-resize"
                  aria-label="Toggle chat"
                >
                  <MessageCircle className="h-4 w-4 stroke-[1.75] group-hover:hidden" />
                  <PanelLeftClose className="h-4 w-4 stroke-[1.75] hidden group-hover:block" />
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Toolbar Share Dialog - opens in parent/simple view for video files only */}
      <ShareDialog
        open={isToolbarShareDialogOpen}
        onOpenChange={setIsToolbarShareDialogOpen}
      />
    </header>
  );
};

export default CustomToolbar;
